import { UserSearch } from 'iconsax-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { Checkbox } from '@src/design-system/checkbox';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import { splitLeadName } from '@src/shared/utils/split-lead-name';
import type { TLead } from '@shared/types/lead';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';

export function SelectedLeadsPopover() {
  const { table } = useTiclickTableContext<TLead>();
  const selectedLeads = table
    .getSelectedRowModel()
    .rows.flat()
    .map((row) => row.original as TLead);

  const handleDeselect = (leadId: number) => {
    const row = table
      .getRowModel()
      .rows.find((item) => (item.original as TLead).id === leadId);
    row?.toggleSelected(false);
  };

  return (
    <Popover modal>
      <PopoverTrigger
        className={cn(
          inputVariants({ variant: 'fill', size: 'lg' }),
          'flex w-full cursor-pointer items-center justify-between text-sm'
        )}
      >
        <span className='flex items-center gap-2'>
          <UserSearch
            size={20}
            color='#584777'
          />
          Show Selected Leads ({selectedLeads.length})
        </span>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-[var(--radix-popover-trigger-width)] p-0'
      >
        <Command>
          <CommandInput
            containerClassName='rounded-none mb-0 border-0 border-b-1 border-pale-border'
            placeholder='Search for leads'
          />
          <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
            <CommandEmpty>No leads found</CommandEmpty>
            <CommandGroup className='p-0'>
              {selectedLeads.map((lead) => {
                const [firstName] = splitLeadName(lead.name || '');

                return (
                  <CommandItem
                    key={lead.id}
                    value={`${lead.name} ${lead.email} ${lead.phone_number}`}
                    className='py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none'
                    data-selected={false}
                    onSelect={() => handleDeselect(lead.id)}
                  >
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        checked
                        onCheckedChange={(checked) => {
                          if (!checked) handleDeselect(lead.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className='flex items-center gap-2'>
                        <Avatar className='size-6'>
                          <AvatarImage src={lead.student?.avatar} />
                          <AvatarFallback colorId={lead.student?.id}>
                            {firstName.substring(0, 1)}
                          </AvatarFallback>
                        </Avatar>
                        <p className='text-sm text-gray-900'>{lead.name}</p>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
